import type { TagCategoriesConfig } from '@racemap/sdk/schema/events';
import { HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import {
  AuthorizationStates,
  EventTypes,
  LeaderboardType,
  ModulesStates,
  VisibilityStates,
} from '@racemap/utilities/consts/events';
import { getSplitsOfTrack } from '@racemap/utilities/functions/event';
import {
  type Geofence,
  type PreparedRanksPackage,
  type RanksPackage,
  RanksQueryFrom,
  type TagsCollection,
} from '@racemap/utilities/functions/timing';
import { isGroupEvent } from '@racemap/utilities/functions/utils';
import { EventLoadTypes, type RacemapEvent } from '@racemap/utilities/types/events';
import type { SplitObject } from '@racemap/utilities/types/geos';
import type { Splits } from '@racemap/utilities/types/types';
import { type Draft, type Immutable, produce } from 'immer';
import { create } from 'zustand';
import { getLoadSource } from '../../lib/loadSource';
import { prepareStarterInfo } from './leaderboard_helper';

const host =
  window.location.host === 'racemap.com' ? 'https://edge.racemap.com' : window.location.origin;
const apiClient = new RacemapAPIClient(host, { includeCredentials: false });

export interface EventData {
  eventObject: RacemapEvent;
  splits: Splits;
  tags: TagsCollection;
  starterData: PreparedRanksPackage;
  timekeepings: Array<Geofence>;
}

export interface LeaderboardState {
  events: Map<string, EventData>;
  isLoading: boolean;
  isFirstLoad: boolean;
  // HTTP status of a failed event load (e.g. 403/404), or null status for
  // network/unknown errors. null when the last load did not fail.
  loadError: { status: number | null } | null;
  selectedEventId: string | null;
  parentEventId: string | null;
  selectedEventSplits: Array<SplitObject> | null;
  registeredLoads: Set<string>;
  actions: {
    loadEventData: (slug: string) => Promise<void>;
    changeSelectedEvent: (eventId: string | null) => Promise<void>;
    clearEventStore: () => void;
    registerLoad: () => void;
    syncEventObject: (event: RacemapEvent) => void;
  };
}

type DraftState = Draft<LeaderboardState>;

const useLeaderboardStore = create<LeaderboardState>()((set, get) => ({
  events: new Map(),
  isLoading: false,
  isFirstLoad: true,
  loadError: null,
  parentEventId: null,
  selectedEventId: null,
  selectedEventSplits: null,
  registeredLoads: new Set<string>(),
  actions: {
    clearEventStore: () => {
      set(
        produce((s: DraftState) => {
          s.events.clear();
          s.loadError = null;
        }),
      );
    },
    loadEventData: async (slug) => {
      set(
        produce((s: DraftState) => {
          s.isLoading = true;
          s.loadError = null;
        }),
      );

      try {
        const mainEvent = await apiClient.getEvent(slug);
        let events: Array<RacemapEvent> = [mainEvent];
        const selectedEventId = get().selectedEventId || mainEvent.id;
        const selectedEventSplits = await apiClient.getEventSplits(selectedEventId);

        switch (mainEvent.type) {
          case EventTypes.CONTEST_GROUP:
          case EventTypes.STAGE_GROUP: {
            const childEvents = await apiClient.getEvents({
              findByParentId: mainEvent.id,
              show: ['hidden'],
            });
            if (childEvents.length === 0) break;

            events.push(...childEvents);
            break;
          }
          case EventTypes.CONTEST:
          case EventTypes.STAGE: {
            if (mainEvent.parent == null) throw new Error('Event of group has no parent id!');

            const [allChildEvents, parentEvent] = await Promise.all([
              apiClient.getEvents({ findByParentId: mainEvent.parent, show: ['hidden'] }),
              apiClient.getEvent(mainEvent.parent),
            ]);

            events = [...allChildEvents, parentEvent];
            break;
          }
        }

        const eventsData = await Promise.all(
          events.map(async (e) => {
            const furtherData = await getFurtherEventData(e);

            return {
              eventObject: e,
              ...(furtherData || {}),
            };
          }),
        );

        // If we have a group event, merge child tag category configs.
        const usedTagCategoriesIndexes = new Set();
        const mergedTagCategoriesConfig = events.reduce<TagCategoriesConfig>((acc, event) => {
          for (const [category, config] of Object.entries(
            event.playerOptions.tagCategoriesConfig,
          )) {
            const existing = acc[category];
            let index = existing?.index ?? config.index;

            while (usedTagCategoriesIndexes.has(index)) {
              index += 1;
            }

            if (existing == null) {
              acc[category] = { ...config, index, default: [...config.default] };
            } else {
              acc[category] = {
                visible: existing.visible || config.visible,
                groupStarterBy: existing.groupStarterBy || config.groupStarterBy,
                index,
                default: Array.from(new Set([...existing.default, ...config.default])),
              };
            }

            usedTagCategoriesIndexes.add(config.index);
          }
          return acc;
        }, {});
        const groupEvent = events.find((e) => isGroupEvent(e));
        if (groupEvent != null)
          groupEvent.playerOptions.tagCategoriesConfig = mergedTagCategoriesConfig;

        set(
          produce((s: DraftState) => {
            for (const data of eventsData) {
              if (data.eventObject.id === selectedEventId) {
                data.splits = selectedEventSplits;
              }
              s.events.set(data.eventObject.id, data);
            }

            if (s.selectedEventId == null) {
              s.selectedEventId = mainEvent.id;
            }
            s.isLoading = false;
            s.isFirstLoad = false;
          }),
        );
      } catch (e) {
        if (e instanceof HTTPFetchError && e.status !== 404 && e.status !== 403) {
          console.error('Error loading event data for leaderboard:', e);
        }
        set(
          produce((s: DraftState) => {
            s.isLoading = false;
            s.isFirstLoad = false;
            s.loadError = { status: e instanceof HTTPFetchError ? e.status : null };
          }),
        );
      }
    },
    changeSelectedEvent: async (eventId) => {
      set(
        produce((s: DraftState) => {
          s.selectedEventId = eventId;
        }),
      );
    },
    syncEventObject: (event) => {
      set(
        produce((s: DraftState) => {
          const existing = s.events.get(event.id);
          if (existing != null) {
            existing.eventObject = event;
          }
        }),
      );
    },
    registerLoad: async () => {
      const selectedEventData = get().events.get(get().selectedEventId || '');
      if (selectedEventData == null) return;

      const selectedEvent = selectedEventData.eventObject;

      // Check if the event is already registered
      if (get().registeredLoads.has(selectedEvent.id)) return;

      // check if visibility, authorization and module state allow loading
      if (selectedEvent.visibility === VisibilityStates.ARCHIVED) return;
      if (selectedEvent.modules.timing.state !== ModulesStates.ACTIVATED) return;
      if (selectedEvent.authorization === AuthorizationStates.NONE) return;
      if (selectedEvent.modules.timing.leaderboard.type !== LeaderboardType.DEFAULT) return;

      await apiClient.triggerEventLoad(selectedEvent.id, {
        loadType: EventLoadTypes.TIMING_LOADS,
        source: getLoadSource(),
      });

      set(
        produce((s: DraftState) => {
          s.registeredLoads.add(selectedEvent.id);
        }),
      );
    },
  },
}));

async function getFurtherEventData(event: Immutable<RacemapEvent>): Promise<{
  starterData: PreparedRanksPackage;
  timekeepings: Array<Geofence>;
  tags: TagsCollection;
  splits: Splits;
}> {
  try {
    if (event.type === EventTypes.CONTEST_GROUP) {
      return {
        starterData: {
          name: event.name,
          startTime: event.startTime,
          endTime: event.endTime,
          geoJSON: '',
          location: event.location,
          image: '',
          timekeepings: [],
          starters: [],
          tagsStarterCount: new Map(),
        },
        timekeepings: [],
        tags: new Map(),
        splits: [],
      };
    }

    let starterData: RanksPackage = {
      name: event.name,
      startTime: event.startTime,
      endTime: event.endTime,
      geoJSON: '',
      location: event.location,
      image: '',
      timekeepings: [],
      starters: [],
    };
    if (
      event.visibility !== VisibilityStates.ARCHIVED &&
      event.modules.timing.state === ModulesStates.ACTIVATED
    ) {
      try {
        starterData = await apiClient.getStarterRanks(event.id, RanksQueryFrom.LEADERBOARD);
      } catch (e) {
        console.error('Error loading starter ranks for event:', event.id, e);
      }
    }

    let tagsCollected = new Map();
    let preparedStarterData: PreparedRanksPackage = {
      ...starterData,
      starters: [],
      tagsStarterCount: new Map(),
    };

    const splits = getSplitsOfTrack(event || null);

    // reduce the tags object of every starter, calculate rank and count starter of every tag
    const sortedStarters = starterData.starters.sort((s1, s2) => {
      if (s1.rank === -1) return 1;
      if (s2.rank === -1) return -1;

      return s1.rank - s2.rank;
    });

    for (const s of sortedStarters) {
      const preparedStarterInfoCol = prepareStarterInfo(
        event,
        s,
        tagsCollected,
        preparedStarterData,
      );

      const preparedStarter = preparedStarterInfoCol.preparedStarter;
      tagsCollected = preparedStarterInfoCol.tagsCollected;
      preparedStarterData = preparedStarterInfoCol.preparedStarterData;

      preparedStarterData.starters.push(preparedStarter);
    }

    const tagsPrepared = new Map();
    for (const [tagKey, tagValues] of tagsCollected.entries()) {
      tagsPrepared.set(
        tagKey,
        Array.from(tagValues).map((t) => ({ value: t })),
      );
    }

    return {
      starterData: preparedStarterData,
      timekeepings: starterData?.timekeepings,
      tags: tagsPrepared,
      splits,
    };
  } catch (e) {
    console.error(e);
    return {
      starterData: {
        name: event.name,
        startTime: event.startTime,
        endTime: event.endTime,
        geoJSON: '',
        location: event.location,
        image: '',
        timekeepings: [],
        starters: [],
        tagsStarterCount: new Map(),
      },
      timekeepings: [],
      tags: new Map(),
      splits: [],
    };
  }
}

const useEvents = () => useLeaderboardStore((state) => state.events);
export const useSelectedEventId = () => useLeaderboardStore((state) => state.selectedEventId);
export const useIsLoading = () => useLeaderboardStore((state) => state.isLoading);
export const useIsFirstLoad = () => useLeaderboardStore((state) => state.isFirstLoad);
export const useLoadError = () => useLeaderboardStore((state) => state.loadError);
export const useLeaderboardActions = () => useLeaderboardStore((state) => state.actions);

export const useRelevantEventsData = (): Map<string, Immutable<EventData>> => {
  const relevantEventsData: Map<string, Immutable<EventData>> = new Map();
  const allEvents = useEvents();

  for (const e of allEvents.values()) {
    relevantEventsData.set(e.eventObject.id, e);
  }

  return relevantEventsData;
};

export const useSelectedEventData = (): Immutable<EventData> | null => {
  const selectedEventId = useSelectedEventId();
  const events = useEvents();
  if (selectedEventId == null) return null;
  const selectedEventData = events.get(selectedEventId) || null;

  return selectedEventData;
};

export const useParentEventData = (): Immutable<EventData> | null => {
  const selectedEventData = useSelectedEventData();
  const events = useEvents();
  if (selectedEventData?.eventObject.type === EventTypes.STAGE_GROUP) return selectedEventData;
  if (selectedEventData == null || selectedEventData.eventObject.parent == null) return null;

  const parentData = events.get(selectedEventData.eventObject.parent);
  return parentData || null;
};
